import type { ReactNode } from 'react';
import clsx from 'clsx';
import type { LabelDef, Tone } from '@/lib/labels';

const tones: Record<Tone, string> = {
  green: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  yellow: 'bg-amber-50 text-amber-800 ring-amber-600/25',
  red: 'bg-red-50 text-red-700 ring-red-600/20',
  blue: 'bg-sky-50 text-sky-700 ring-sky-600/20',
  gray: 'bg-slate-100 text-slate-500 ring-slate-400/20',
  slate: 'bg-slate-50 text-slate-700 ring-slate-500/20',
  orange: 'bg-orange-50 text-orange-700 ring-orange-600/20',
  purple: 'bg-purple-50 text-purple-700 ring-purple-600/20',
  black: 'bg-slate-900 text-white ring-slate-900',
};

const dots: Record<Tone, string> = {
  green: 'bg-emerald-500',
  yellow: 'bg-amber-400',
  red: 'bg-red-500',
  blue: 'bg-sky-500',
  gray: 'bg-slate-300',
  slate: 'bg-slate-400',
  orange: 'bg-orange-500',
  purple: 'bg-purple-500',
  black: 'bg-white',
};

export function Badge({ tone = 'slate', children, dot, className }: { tone?: Tone; children: ReactNode; dot?: boolean; className?: string }) {
  return (
    <span className={clsx('inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset', tones[tone], className)}>
      {dot && <span className={clsx('h-1.5 w-1.5 rounded-full', dots[tone])} />}
      {children}
    </span>
  );
}

export function StatusBadge({ dict, value, dot = true }: { dict: Record<string, LabelDef>; value: string | null | undefined; dot?: boolean }) {
  if (!value) return <span className="text-slate-400">–</span>;
  const d = dict[value] ?? { label: value, tone: 'slate' as Tone };
  return (
    <Badge tone={d.tone} dot={dot}>
      {d.label}
    </Badge>
  );
}

/** Confidence-Anzeige des Bankabgleichs (0–100 %) */
export function ConfidenceBadge({ score }: { score: number | null | undefined }) {
  if (score === null || score === undefined) return <span className="text-slate-400">–</span>;
  const tone: Tone = score >= 95 ? 'green' : score >= 80 ? 'blue' : score >= 50 ? 'yellow' : 'red';
  return (
    <Badge tone={tone} className="tabular">
      {score} %
    </Badge>
  );
}
